import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, tap, catchError, throwError } from 'rxjs';
import { Company, Site } from 'app/core/models/base.model';
import { UserDataService } from 'app/core/datas/user-data';
import { CompanyFields } from 'app/app.config';
import { ApiSiteService } from 'app/core/apis/api-sites.service';
import { ApiCompanyService, ApiCompanySiteService } from 'app/core/apis/api-company.service';

/**
 * AdminService - Manages site-level admin data and operations
 *
 * This service handles site context for the admin module, providing:
 * - Current site ID and companySite (Company) object state management
 * - Company/Site data fetching and caching
 * - Integration with UserDataService for selected sites
 *
 * IMPORTANT: Sites are now represented as Company objects (sub-companies).
 * Use companySite$ instead of site$ for new code.
 */
@Injectable({ providedIn: 'root' })
export class AdminService {
    private readonly _userDataSvc = inject(UserDataService);
    private readonly _apiSiteService = inject(ApiSiteService);
    private readonly _apiCompanyService = inject(ApiCompanyService);
    private readonly _apiCompanySiteService = inject(ApiCompanySiteService);
    private readonly _siteid = new BehaviorSubject<number>(0);
    private readonly _site = new BehaviorSubject<Site | null>(null);  // @deprecated - use _companySite
    private readonly _companySite = new BehaviorSubject<Company | null>(null);

    /**
     * Observable stream of the current site ID
     */
    get siteid$(): Observable<number> {
        return this._siteid.asObservable();
    }

    /**
     * Observable stream of the current site object
     * @deprecated Use companySite$ instead
     */
    get site$(): Observable<Site | null> {
        return this._site.asObservable();
    }

    /**
     * Observable stream of the current companySite (Company) object
     * Use this for site-level admin features (sites are now Companies)
     */
    get companySite$(): Observable<Company | null> {
        return this._companySite.asObservable();
    }

    /**
     * Get the current site ID value (synchronous)
     */
    get currentSiteId(): number {
        return this._siteid.value;
    }

    /**
     * Get the current site object value (synchronous)
     * @deprecated Use currentCompanySite instead
     */
    get currentSite(): Site | null {
        return this._site.value;
    }

    /**
     * Get the current companySite (Company) object value (synchronous)
     */
    get currentCompanySite(): Company | null {
        return this._companySite.value;
    }

    /**
     * Update the current site ID
     * @param id The site ID to set
     */
    updateSiteId(id: number): void {
        this._siteid.next(id);
    }

    /**
     * Update the current site object
     * @param site The site object to set
     * @deprecated Use updateCompanySite instead
     */
    updateSite(site: Site | null): void {
        this._site.next(site);
    }

    /**
     * Update the current companySite (Company) object
     * @param companySite The Company object to set
     */
    updateCompanySite(companySite: Company | null): void {
        this._companySite.next(companySite);
    }

    /**
     * Fetch site data from the API by ID
     *
     * This method:
     * 1. Fetches the site data with project fields populated
     * 2. Updates UserDataService selected sites cache
     * 3. Updates the current site in this service
     * 4. Includes error handling with logging
     *
     * @param id The site ID to fetch
     * @param params Optional additional query parameters
     * @returns Observable of the Site object
     * @deprecated Use getRemoteCompanySiteById instead
     */
    getRemoteSiteById(id: number, params: Record<string, any> = {}): Observable<Site> {
        const defaultParams = {
        };
        const mergedParams = { ...defaultParams, ...params };

        return this._apiSiteService.apiGetObject(id, mergedParams).pipe(
            tap({
                next: (response: Site) => {
                    this._userDataSvc.addSelectedSite(response);
                    this.updateSite(response);
                    this.updateSiteId(id);
                },
                error: (error) => {
                    console.error(`[AdminService] Failed to fetch site ${id}:`, error);
                }
            }),
            catchError((error) => {
                // Clear site state on error
                this.updateSite(null);
                return throwError(() => error);
            })
        );
    }

    /**
     * Fetch companySite (Company) data from the API by site ID
     *
     * This method:
     * 1. Fetches the company data with project fields populated
     * 2. Updates UserDataService selected companySites cache
     * 3. Updates the current companySite in this service
     * 4. Includes error handling with logging
     *
     * Use this method for site-level admin features (sites are now Companies)
     *
     * @param id The site/company ID to fetch
     * @param params Optional additional query parameters
     * @returns Observable of the Company object
     */
    getRemoteCompanySiteById(id: number, params: Record<string, any> = {}): Observable<Company> {
        const defaultParams = {
            fields: CompanyFields,
            parent_fields: 'id,app_settings',
        };
        const mergedParams = { ...defaultParams, ...params };

        return this._apiCompanySiteService.apiGetObject(id, mergedParams).pipe(
            tap({
                next: (response: Company) => {
                    this._userDataSvc.addSelectedCompanySite(response);
                    this.updateCompanySite(response);
                    this.updateSiteId(id);
                },
                error: (error) => {
                    console.error(`[AdminService] Failed to fetch companySite ${id}:`, error);
                }
            }),
            catchError((error) => {
                // Clear companySite state on error
                this.updateCompanySite(null);
                return throwError(() => error);
            })
        );
    }

    /**
     * Clear the current site state
     * @deprecated Use clearCompanySite instead
     */
    clearSite(): void {
        this._siteid.next(0);
        this._site.next(null);
    }

    /**
     * Clear the current companySite state
     */
    clearCompanySite(): void {
        this._siteid.next(0);
        this._companySite.next(null);
    }

}